import { ChangeDetectionStrategy, Component, computed, HostBinding, input, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { AnimationDuration, getAnimationAppearDisappearWithHeight } from '@examdojo/animation';
import { Icon, IconComponent } from '@examdojo/core/icon';
import { IconButtonComponent } from '@examdojo/core/icon-button';
import { distinctUntilDeepChanged } from '@examdojo/rxjs';
import { skip, tap } from 'rxjs';
import { BannerConfig, BannerType, isBannerHtmlConfig } from './banner.model';
import { BannerStorageService } from './banner.service';

interface BannerTypeConfig {
  containerClass: string;
  icon: Icon;
}

const BANNER_TYPE_CONFIG: Record<BannerType, BannerTypeConfig> = {
  [BannerType.Warning]: {
    containerClass: 'border-b-amber-200 bg-amber-50 text-amber-700',
    icon: 'triangle-exclamation',
  },
  [BannerType.Info]: {
    containerClass: 'border-b-neutral-200 bg-neutral-50 text-neutral-700',
    icon: 'circle-info',
  },
};

const DEFAULT_CONTAINER_CLASS = 'border-b-neutral-200 bg-neutral-50 text-neutral-700';

@Component({
  selector: 'dojo-banner',
  imports: [IconComponent, IconButtonComponent],
  templateUrl: './banner.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [BannerStorageService],
  host: { class: 'block w-full' },
  animations: [
    getAnimationAppearDisappearWithHeight({ enter: AnimationDuration.Medium, leave: AnimationDuration.Long }),
  ],
})
export class BannerComponent {
  constructor(private readonly bannerService: BannerStorageService) {
    this.resetDismissedOnContentChange().pipe(takeUntilDestroyed()).subscribe();
  }

  readonly content = input.required<BannerConfig>();
  readonly dataTest = input<string | null>(null);

  readonly bannerType = computed<BannerType | null>(() => this.content().type ?? null);

  readonly bannerMessage = computed(() => {
    const config = this.content();
    return isBannerHtmlConfig(config) ? config.html : config.message;
  });

  readonly isHtml = computed(() => isBannerHtmlConfig(this.content()));

  readonly isDismissible = computed(() => this.content().dismissible === true);

  readonly bannerConfig = computed<BannerTypeConfig | null>(() => {
    const type = this.bannerType();
    return type ? BANNER_TYPE_CONFIG[type] : null;
  });

  readonly containerClass = computed(() => {
    return this.bannerConfig()?.containerClass ?? DEFAULT_CONTAINER_CLASS;
  });

  private readonly dismissedByUser = signal(false);

  readonly dismissed = computed(
    () => this.dismissedByUser() || (this.isDismissible() && this.bannerService.isDismissed(this.bannerMessage())),
  );

  @HostBinding('@appearDisappearWithHeight') protected readonly animation = true;
  @HostBinding('attr.data-test') get dataTestAttr() {
    return this.dataTest();
  }

  dismiss(): void {
    this.bannerService.dismiss(this.bannerMessage());
    this.dismissedByUser.set(true);
  }

  private resetDismissedOnContentChange() {
    return toObservable(this.content).pipe(
      skip(1),
      distinctUntilDeepChanged(),
      tap(() => this.dismissedByUser.set(false)),
    );
  }
}
